// scene/stars3d.js -- the stars as places, not as a picture (spec 0028 step 3).
//
// Contract: createStars3d(scene, opts) -> { load, ensureGeometry, records, count, unplaced,
//   setVisible, setOpacity, setMagLimit, setStretch, stretch, rebuild, update, pickAll, dispose }
//
// 109 389 stars from HYG v4.4 (site/data/stars3d.bin, scripts/build-stars3d.py) as ONE Points
// cloud. Two things decide how it is drawn, and both are the stage's (spec 0005):
//
//   TRUE mode   -- on a rung of the ladder (stage.js `ladder`), every star sits at its measured
//                  position, one unit = the rung's unit_km, and its size comes from its ABSOLUTE
//                  magnitude and its distance from the camera, computed on the GPU. Fly towards
//                  Sirius and it brightens; leave the Sun behind and it becomes one more star.
//   SHELL mode  -- on a world stage the true positions are past the far plane (Proxima is 4e10
//                  units from Earth), so the same stars are drawn as DIRECTIONS on a shell that
//                  follows the camera, sized by apparent magnitude. That is the sky sphere again,
//                  with 109 389 stars instead of 5 044 -- and registry/lod.yaml fades it in only
//                  once the sky-from-here has faded out, so no star is ever drawn twice.
//
// COST. Static: the buffers are built once per stage change (109 389 frame conversions, float64,
// no allocation) and nothing is touched per frame but a uniform and, in shell mode, the group's
// position. The binary (2.6 MB) is fetched the first time a star could actually be seen; the
// names file (3 390 named stars with positions, 0.3 MB) is what search and the card need, and it
// loads with the layer -- which since 2026-10-01 is after the first visit (main.js OFF THE FIRST
// VISIT): a few seconds after the map's other data, or at once on the ladder or in search.
//
// HONESTY. Only stars with a measured distance are here; the 10 224 HYG rows without one are
// counted (`unplaced()`) and never drawn on an invented shell. A tap on an unnamed star yields a
// record that says it is unnamed, with the HYG row number.

import * as THREE from '../../vendor/three.module.min.js';
import { stage, isLadderStage } from './stage.js';
import { bvToKelvin, kelvinToRgb, starTint } from './starfield.js';
import { COPY, t } from '../copy/en.js';
import { STARS_NOTABLE } from '../data/starsnotable.js';
import { drawnPositions } from './clusters.js';
import { HOLES_GLSL_HEAD, holeUniforms, setHoles as setHoleUniforms } from './starholes.js';
import { createPickIndex } from './pickindex.js';
import { STRETCH_PX, STRETCH_VERT_HEAD, STRETCH_VERT, STRETCH_FRAG_HEAD, STRETCH_FRAG, STAR_LIGHT_GLSL, stretchUniforms, writeStretch } from './stretch.js';

// Spec 0034: the star-stretch's length lives in scene/stretch.js (both star draws read it) and is
// re-exported here, where docs/design-language.md's table says to look for it.
export { STRETCH_PX };

export const LY_KM = 9460730472580.8;
/**
 * How many named stars data/stars3d.names.json holds (scripts/build-stars3d.py), shipped here so the
 * Stars layer can state its number before the file has loaded (main.js OFF THE FIRST VISIT).
 * tests/test_stars3d.mjs holds it to the file: rebuild one, update the other.
 */
export const NAMED_STARS = 3390;
// The Sun's absolute visual magnitude (IAU 2015 B3's V-band value, 4.83), and its B-V (0.65).
export const SUN_ABS_MAG = 4.83;
const SUN_BV = 0.65;
export const PC_KM = 30856775814913.67;
const SHELL_UNITS = 1e8; // the shell's radius in scene units: far inside the 1e9 far plane
const RECORD_BYTES = 24;
const HEADER_BYTES = 16;
const PICK_PX = 24;
const PICK_MIN_OPACITY = 0.15; // below this the layer is a ghost of itself (a cross-fade's end), not something to tap

/** The magnitude the shader cuts on (VERT above): the catalogue's in shell mode, the one at the camera otherwise. Pure. */
export function apparentMagOf(appMag, absMag, shell, x, y, z, cam, unitsPerPc) {
  if (shell) return appMag;
  const dpc = Math.max(1e-9, Math.hypot(x - cam.x, y - cam.y, z - cam.z) / unitsPerPc);
  return absMag + 5 * Math.log10(dpc / 10);
}
const SUN_INERTIAL = 'sun-inertial';

const VERT = /* glsl */ `
${HOLES_GLSL_HEAD}
attribute float aAbsMag;
attribute float aAppMag;
attribute vec3 aColour;
uniform float uPixelRatio;
uniform float uGain;
uniform float uScale;      // 1; larger while a constellation figure is up (setPointScale)
uniform float uMagLimit;
uniform float uShell;
uniform float uUnitsPerPc;
varying vec3 vColour;
varying float vAlpha;
varying float vCore;
varying float vGlare;
varying float vGlow;
${STRETCH_VERT_HEAD}
void main() {
  vec4 mv = modelViewMatrix * vec4( position, 1.0 );
  gl_Position = projectionMatrix * mv;
  float m;
  if ( uShell > 0.5 ) {
    m = aAppMag;
  } else {
    // apparent magnitude at the CAMERA: M + 5 log10( d / 10 pc )
    float dpc = max( 1e-9, length( position - cameraPosition ) / uUnitsPerPc );
    m = aAbsMag + 5.0 * log( dpc / 10.0 ) / log( 10.0 );
  }
  // the same curve as starfield.js magToSize / magToAlpha, extended one magnitude fainter
  float tt = clamp( ( 6.5 - m ) / 8.0, 0.0, 1.0 );
  float core = 1.0 + 5.0 * pow( tt, 1.6 );
  // BRIGHTER THAN ANY STAR IN EARTH'S SKY. That curve tops out at -1.5, Sirius from here, because
  // the sky from Earth never goes further. On the ladder you can fly close: Sirius from a light-year
  // out is magnitude -6, brighter than Venus, and was drawn the same 6 px speck as a star of -1.5
  // while the exoplanet marks around it were bigger (measured on the "To the edge" trip,
  // 2026-09-22). So past -1.5 the core stays its size and a glare grows round it, the way the eye
  // reports a light too bright to resolve. At -1.5 and fainter nothing changes: glare is 0.
  float glare = clamp( ( -1.5 - m ) / 6.0, 0.0, 1.0 );
  // A GLOW ROUND THE BRIGHT ONES (public #271, scene/stretch.js A STAR'S LIGHT): from magnitude 2.5
  // up to -1.5 the sprite grows to five times the core, a magnitude earlier than scene/starfield.js, so
  // the cluster a camera stands beside is a handful of lights and not a handful of dots.
  float glow = clamp( ( 2.5 - m ) / 4.0, 0.0, 1.0 );
  core *= 1.0 + 0.8 * glow;   // and the peak itself a little wider, or a first-magnitude star is still a speck
  float size = core * ( 1.0 + 4.0 * glow ) + 26.0 * glare;
  float sizePx = size * uPixelRatio * uScale;
  // Spec 0034: during a ladder flight the point becomes a capsule along its own screen motion;
  // at uStretch == 0 this is gl_PointSize = sizePx, as before.
${STRETCH_VERT}
  vCore = core / size;
  vGlare = glare;
  vGlow = glow;
  vAlpha = ( m > uMagLimit ) ? 0.0 : ( 0.35 + 0.65 * tt ) * uGain;
  // Where a photograph is drawn our stars step aside (scene/starholes.js); no hole, no change.
  if ( uHoleCount > 0 ) vAlpha *= starHole( normalize( mv.xyz ) );
  vColour = aColour;
}
`;

const FRAG = /* glsl */ `
varying vec3 vColour;
varying float vAlpha;
varying float vCore;
varying float vGlare;
varying float vGlow;
${STRETCH_FRAG_HEAD}
#include <common>
${STAR_LIGHT_GLSL}
void main() {
  if ( vAlpha <= 0.0 ) discard;
  // The distance to the star's segment (a point when it is not stretched), in the disc's units.
${STRETCH_FRAG}
  // The core in its own units, so it is the same number of pixels with or without a glow.
  vec2 light = starLight( d, vCore, vGlow );
  float a = light.x + light.y;
  if ( vGlare > 0.0 ) {
    float e = 1.0 - clamp( d / 0.5, 0.0, 1.0 );
    a = max( a, vGlare * 0.8 * e * e );
  }
  if ( a <= 0.003 ) discard;
  gl_FragColor = vec4( mix( vColour, vec3( 1.0 ), 0.4 * light.x * max( vGlow, vGlare ) ), a * vAlpha * taper );
  #include <colorspace_fragment>
}
`;

/** Parse the binary. Exported so a test can hold the format without a scene. */
export function parseStars3d(buffer) {
  const dv = new DataView(buffer);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== 'SR3D') throw new Error(`stars3d.bin: bad magic ${JSON.stringify(magic)}`);
  const version = dv.getUint32(4, true);
  const count = dv.getUint32(8, true);
  const unplaced = dv.getUint32(12, true);
  if (buffer.byteLength !== HEADER_BYTES + count * RECORD_BYTES) throw new Error('stars3d.bin: length does not match its count');
  const posLy = new Float32Array(count * 3);
  const absMag = new Float32Array(count);
  const appMag = new Float32Array(count);
  const ci = new Float32Array(count);
  const nameRef = new Uint16Array(count);
  for (let i = 0; i < count; i++) {
    const o = HEADER_BYTES + i * RECORD_BYTES;
    posLy[i * 3] = dv.getFloat32(o, true);
    posLy[i * 3 + 1] = dv.getFloat32(o + 4, true);
    posLy[i * 3 + 2] = dv.getFloat32(o + 8, true);
    absMag[i] = dv.getFloat32(o + 12, true);
    appMag[i] = dv.getFloat32(o + 16, true);
    const c = dv.getInt16(o + 20, true);
    ci[i] = c === -32768 ? NaN : c / 1000;
    nameRef[i] = dv.getUint16(o + 22, true);
  }
  return { version, count, unplaced, posLy, absMag, appMag, ci, nameRef };
}

// FAMOUS STARS (registry/stars-notable.yaml, 2026-09-22). No star record carried `meta.why`, so
// ui/labels.js named a star on the ladder only once it was selected, and a card for Vega said what
// any star's card says. A row keys on the HIP number the record id is built from; the one star with
// no HIP number (Wolf 359) keys on its exact proper name, which check_registry.py proves is unique.
const FAMOUS_BY_HIP = new Map(STARS_NOTABLE.filter((s) => s.hip).map((s) => [s.hip, s]));
const FAMOUS_BY_PROPER = new Map(STARS_NOTABLE.filter((s) => !s.hip && s.proper).map((s) => [s.proper, s]));

/** The registry row for a names-file row, or null. */
function famousRow(proper, hip) {
  if (hip) return FAMOUS_BY_HIP.get(hip) || null;
  return proper ? FAMOUS_BY_PROPER.get(proper) || null : null;
}

/** One record per named star, from the names file. Exported for the test and for search. */
export function recordsFromNames(rows) {
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const [idx, proper, bayer, flam, hip, spect, distLy, mag, lum, x, y, z, iau] = r; // 0079 r15-a45: the IAU's name, joined in by data/a45.js withIauNames
    if (!Number.isFinite(x)) continue;
    const catalogueName = proper || (iau && iau.name) || bayer || flam || (hip ? `HIP ${hip}` : null); // 0079 r15-a45
    if (!catalogueName) continue;
    const famous = famousRow(proper, hip);
    // The name a person uses wins over the names file's (Tau Ceti over "τ Cet", Alpha Centauri A
    // over Rigil Kentaurus): search lists record.name and the label and card print it. The file's
    // own name stays first among the aliases, so "rigil kentaurus" and "ran" still find their star.
    const name = famous && famous.name ? famous.name : catalogueName;
    const aliases = [...new Set([catalogueName, bayer, flam, hip ? `HIP ${hip}` : null, iau ? iau.name : null, iau ? iau.designation : null])].filter((a) => a && a !== name); // 0079 r15-a45: + the IAU's name and designation
    const meta = { hip, spect, distLy, mag, lum, aliases, named: true };
    if (iau) meta.iau = iau; // 0079 r15-a45
    if (famous) {
      meta.why = famous.why; meta.whySource = famous.source;
      // A width read off an interferometer, with its source (registry/stars-notable.yaml): the card then says "measured".
      if (famous.radius_suns > 0) { meta.measuredRadiusSuns = famous.radius_suns; meta.measuredTeffK = famous.teff_k; meta.widthSource = famous.physical_source; }
    }
    out.push(starRecord(idx, name, { x, y, z }, meta));
  }
  return out;
}

// The Sun's nominal radius, effective temperature and absolute bolometric magnitude (IAU 2015 B3, B2).
export const SUN_RADIUS_KM = 695700;
export const SUN_TEFF_K = 5772;
const SUN_ABS_BOL = 4.74;

/**
 * The bolometric correction in V for a temperature: Flower (1996, ApJ 469, 355) with the
 * coefficients as Torres (2010, AJ 140, 1158) corrected them. -0.08 for the Sun, -0.25 at
 * 10 000 K. Under 3 300 K the polynomial runs away, so it is held there.
 */
export function bolometricCorrection(teffK) {
  const l = Math.log10(Math.max(3300, teffK));
  const a = l < 3.7 ? [-0.190537291496456e5, 0.155144866764412e5, -0.421278819301717e4, 0.381476328422343e3]
    : l < 3.9 ? [-0.370510203809015e5, 0.385672629965804e5, -0.150651486316025e5, 0.261724637119416e4, -0.170623810323864e3]
      : [-0.118115450538963e6, 0.137145973583929e6, -0.636233812100225e5, 0.147412923562646e5, -0.170587278406872e4, 0.78873172180499e2];
  let bc = 0;
  for (let i = a.length - 1; i >= 0; i -= 1) bc = bc * l + a[i];
  return bc;
}

/**
 * How wide a star is, worked out from the two numbers the catalogue has for it: its absolute
 * magnitude and its B-V colour. Colour gives a temperature (starfield.js bvToKelvin), the
 * temperature a bolometric correction, the two a luminosity, and a black body of that luminosity
 * and temperature has one radius (Stefan-Boltzmann). Pure.
 *
 * AN ESTIMATE, and the card says so. Checked 2026-10-06 against measured radii: Sirius 1.69 Suns
 * (measured 1.71), the Sun 0.99, Vega 2.5 (2.4 to 2.8, it spins), Arcturus 27 (25). For the reddest
 * stars B-V stops telling temperatures apart and the answer can be off by a factor of two
 * (Betelgeuse about 1 550 against 640 to 760 measured, Proxima 0.07 against 0.15).
 */
export function starPhysical(absMag, bv) {
  if (!Number.isFinite(absMag)) return null;
  const teffK = Math.max(3300, bvToKelvin(Number.isFinite(bv) ? bv : 0.65));
  const lum = 10 ** (0.4 * (SUN_ABS_BOL - (absMag + bolometricCorrection(teffK))));
  return { radiusKm: SUN_RADIUS_KM * Math.sqrt(lum) * (SUN_TEFF_K / teffK) ** 2, teffK, how: 'estimated' };
}

function starRecord(idx, name, ly, meta) {
  return {
    id: meta.hip ? `hip-${meta.hip}` : `hyg-${idx}`,
    name,
    klass: 'star',
    layer: 'stars',
    propagator: 'static',
    frame: SUN_INERTIAL,
    pos: { x: ly.x * LY_KM, y: ly.y * LY_KM, z: ly.z * LY_KM },
    cls: 'measured',
    meta: { starIndex: idx, cite: 'HYG Stellar Database v4.4 (CC BY-SA 4.0), Gaia and Hipparcos distances', ...meta },
  };
}

export function createStars3d(scene, opts = {}) {
  const here = typeof import.meta !== 'undefined' ? import.meta.url : undefined;
  const src = {
    bin: opts.bin ?? (here ? new URL('../../data/stars3d.bin', here) : 'data/stars3d.bin'),
    names: opts.names ?? (here ? new URL('../../data/stars3d.names.json', here) : 'data/stars3d.names.json'),
  };
  const group = new THREE.Group();
  group.name = 'stars3d';
  group.renderOrder = -1; // after the sky sphere (-2), before every world
  if (scene) scene.add(group);

  let data = null; // parseStars3d result
  let namedRows = null;
  let byIndex = new Map(); // 0079 r15-a45: named records by star index (the IAU names a star the binary's name column does not)
  let records = [];
  let points = null;
  let geometry = null;
  // THE SUN, AS A STAR. HYG's row for the Sun has no distance and is not in the binary, and on a
  // rung of the ladder nothing else drew it as one: from a light-year out, where it is magnitude
  // -2.7, its place was a purple ringed glyph of the probes stacked on its pixel (2026-09-22). One
  // more point with the same shader, on the ladder only -- from a world stage the Sun is a disc.
  let sunPoints = null;
  let layerOn = true;
  let opacity = 0;
  let mode = null; // 'true' | 'shell'
  let builtFor = null; // `${stage.worldId}:${mode}`
  let loadingBin = null;
  const uniforms = {
    ...holeUniforms(),
    uPixelRatio: { value: 1 },
    uScale: { value: 1 },
    uGain: { value: 0 },
    uMagLimit: { value: 7.5 },
    uShell: { value: 1 },
    uUnitsPerPc: { value: 1 },
    // Spec 0034: 0 unless ui/trip.js is flying a ladder flight longer than three seconds.
    ...stretchUniforms(),
  };

  // `no-cache` = revalidate against the server (a 304 when unchanged): the files keep their names
  // when a rebuild changes them, so a plain cached copy could be a month stale.
  async function fetchJson(u) { const r = await fetch(String(u), { cache: 'no-cache' }); if (!r.ok) throw new Error(`${u}: HTTP ${r.status}`); return r.json(); }
  async function fetchBin(u) { const r = await fetch(String(u), { cache: 'no-cache' }); if (!r.ok) throw new Error(`${u}: HTTP ${r.status}`); return r.arrayBuffer(); }

  /** The named stars, as records: what the layer, search and the card work from. Small file. */
  async function load() {
    if (namedRows) return records;
    const doc = opts.namesDoc || await fetchJson(src.names);
    namedRows = Array.isArray(doc && doc.rows) ? doc.rows : [];
    // 0079 r15-a45: begin -- the IAU Working Group on Star Names' list, joined to the names (data/a45.js); a miss leaves the names as they were
    let noDistance = [];
    try {
      const a45 = await import('../data/a45.js');
      const iauDoc = opts.iauDoc || (opts.namesDoc ? null : await a45.loadIau());
      if (iauDoc) namedRows = a45.withIauNames(namedRows, iauDoc);
      // the 63 named stars HYG has a direction for and no distance (scene/unplaced.js draws the rest): searchable, with no place in space
      const unplacedDoc = opts.unplacedNames || (opts.namesDoc ? null : await a45.loadUnplacedNames());
      if (unplacedDoc) noDistance = a45.unplacedNamedRecords(unplacedDoc);
    } catch (e) { console.warn('stars3d: the IAU names did not load', e); }
    // 0079 r15-a45: end
    records = recordsFromNames(namedRows).concat(noDistance); // 0079 r15-a45
    byIndex = new Map(records.filter((r) => r.meta.starIndex >= 0).map((r) => [r.meta.starIndex, r])); // 0079 r15-a45
    regroupRecords();
    return records;
  }

  /** The binary, once, the first time a star could be seen. */
  function ensureGeometry() {
    if (data) return Promise.resolve(data);
    if (loadingBin) return loadingBin;
    loadingBin = (opts.binBuffer ? Promise.resolve(opts.binBuffer) : fetchBin(src.bin))
      .then((buf) => {
        data = parseStars3d(buf);
        // The Pleiades' stars, gathered at the cluster's distance (scene/clusters.js says why).
        // data.posLy stays the catalogue's; drawLy is what the buffers and the taps use.
        Object.assign(data, drawnPositions(data.posLy, data.count));
        regroupRecords();
        buildPoints();
        rebuild();
        return data;
      })
      .catch((err) => { console.warn('stars3d: could not load', err); loadingBin = null; return null; });
    return loadingBin;
  }

  /** A named member of a regrouped cluster is marked where it is drawn; its card keeps the catalogue's distance. */
  function regroupRecords() {
    if (!data || !data.moved || !data.moved.size) return;
    for (const rec of records) {
      const i = rec && rec.meta ? rec.meta.starIndex : -1;
      if (!data.moved.has(i) || rec.meta.drawnIn) continue;
      rec.pos = { x: data.drawLy[i * 3] * LY_KM, y: data.drawLy[i * 3 + 1] * LY_KM, z: data.drawLy[i * 3 + 2] * LY_KM };
      rec.meta.drawnIn = `dso-${data.moved.get(i)}`;
    }
  }

  function buildPoints() {
    const n = data.count;
    geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geometry.setAttribute('aAbsMag', new THREE.BufferAttribute(data.absMag, 1));
    geometry.setAttribute('aAppMag', new THREE.BufferAttribute(data.appMag, 1));
    const col = new Float32Array(n * 3);
    const c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const bv = Number.isFinite(data.ci[i]) ? data.ci[i] : 0.65; // no colour index: draw it Sun-like
      const rgb = starTint(bv);
      c.setRGB(rgb[0], rgb[1], rgb[2], THREE.SRGBColorSpace);
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    }
    geometry.setAttribute('aColour', new THREE.BufferAttribute(col, 3));
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms,
      // Opaque list on purpose, like starfield.js: `transparent: true` would draw after Earth.
      transparent: false,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    points = new THREE.Points(geometry, material);
    points.name = 'stars3d';
    points.frustumCulled = false;
    points.renderOrder = -1;
    group.add(points);

    const sun = new THREE.BufferGeometry();
    sun.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3));
    sun.setAttribute('aAbsMag', new THREE.BufferAttribute(new Float32Array([SUN_ABS_MAG]), 1));
    sun.setAttribute('aAppMag', new THREE.BufferAttribute(new Float32Array([99]), 1)); // never on the shell
    const sunRgb = kelvinToRgb(bvToKelvin(SUN_BV));
    c.setRGB(sunRgb[0], sunRgb[1], sunRgb[2], THREE.SRGBColorSpace);
    sun.setAttribute('aColour', new THREE.BufferAttribute(new Float32Array([c.r, c.g, c.b]), 3));
    sunPoints = new THREE.Points(sun, material);
    sunPoints.name = 'stars3d:sun';
    sunPoints.frustumCulled = false;
    sunPoints.renderOrder = -1;
    group.add(sunPoints);
    applyVisibility();
  }

  const _km = { x: 0, y: 0, z: 0 };
  const _km0 = { x: 0, y: 0, z: 0 };
  const _v = new THREE.Vector3();

  /**
   * Fill the position attribute for the current stage. TRUE positions on a rung; unit directions
   * on the shell on a world stage. Called on `sr:stage` and once after the binary arrives.
   */
  function rebuild() {
    if (!data || !geometry) return;
    const nextMode = isLadderStage(stage.worldId) ? 'true' : 'shell';
    const key = `${stage.worldId}:${nextMode}`;
    if (key === builtFor) return;
    mode = nextMode;
    builtFor = key;
    const tMs = stage.tMs;
    const pos = geometry.getAttribute('position').array;
    const n = data.count;
    let bad = 0;
    // One matrix for all 109 000 stars (stage.affineFrom): the exact call per star composed the frame
    // each time, 1.1 to 1.7 s on the Earth's stage. Exact calls stay for a stage that bends positions.
    const A = stage.affineFrom(SUN_INERTIAL, tMs);
    for (let i = 0; i < n; i++) {
      if (A) {
        const kx = data.drawLy[i * 3] * LY_KM, ky = data.drawLy[i * 3 + 1] * LY_KM, kz = data.drawLy[i * 3 + 2] * LY_KM;
        _v.set(A.o[0] + A.x[0] * kx + A.y[0] * ky + A.z[0] * kz, A.o[1] + A.x[1] * kx + A.y[1] * ky + A.z[1] * kz, A.o[2] + A.x[2] * kx + A.y[2] * ky + A.z[2] * kz);
      } else {
        _km.x = data.drawLy[i * 3] * LY_KM;
        _km.y = data.drawLy[i * 3 + 1] * LY_KM;
        _km.z = data.drawLy[i * 3 + 2] * LY_KM;
        if (!stage.toSceneInto(_km, SUN_INERTIAL, _v, tMs)) { bad++; pos[i * 3] = pos[i * 3 + 1] = pos[i * 3 + 2] = 0; continue; }
      }
      if (mode === 'shell') _v.normalize().multiplyScalar(SHELL_UNITS);
      pos[i * 3] = _v.x; pos[i * 3 + 1] = _v.y; pos[i * 3 + 2] = _v.z;
    }
    geometry.getAttribute('position').needsUpdate = true;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), mode === 'shell' ? SHELL_UNITS * 1.01 : 1e9);
    uniforms.uShell.value = mode === 'shell' ? 1 : 0;
    uniforms.uUnitsPerPc.value = PC_KM / stage.unitKm;
    if (mode === 'true') group.position.set(0, 0, 0);
    if (sunPoints) {
      const at = sunPoints.geometry.getAttribute('position');
      if (mode === 'true' && stage.toSceneInto(_km0, SUN_INERTIAL, _v, tMs)) { at.array[0] = _v.x; at.array[1] = _v.y; at.array[2] = _v.z; }
      at.needsUpdate = true;
    }
    applyVisibility();
    if (bad) console.warn(`stars3d: ${bad} stars could not be placed in the ${stage.worldId} stage`);
  }

  function applyVisibility() {
    if (points) points.visible = layerOn && opacity > 0;
    if (sunPoints) sunPoints.visible = !!(points && points.visible) && mode === 'true';
    uniforms.uGain.value = opacity;
  }

  /**
   * How large the 3D stars' points are drawn, 1 = as ever: the sky sphere's stars step forward while a constellation
   * figure is up (scene/figures3d.js STAR_SCALE), and the cloud they hand over to does the same (internal #387).
   */
  /** The photographs on screen this frame (scene/nebulae.js holes()): our stars step aside inside them. */
  function setHoles(list) { return setHoleUniforms(uniforms, list); }
  function setPointScale(k) { uniforms.uScale.value = Math.min(3, Math.max(0.5, Number(k) || 1)); }

  /** The faintest star drawn, in apparent magnitude: the shutter's (scene/exposure.js starLimit). Capped at the file's 7.5. */
  function setMagLimit(m) { const x = Number(m); uniforms.uMagLimit.value = Number.isFinite(x) ? Math.min(7.5, Math.max(0, x)) : 7.5; }

  /** The layer's checkbox. */
  function setVisible(on) { layerOn = on !== false; applyVisibility(); if (layerOn && opacity > 0) ensureGeometry(); }

  /** registry/lod.yaml's `stars-3d` hook, 0..1. Fetches the binary the first time it is above 0. */
  function setOpacity(k) {
    opacity = Math.min(1, Math.max(0, Number(k) || 0));
    applyVisibility();
    if (layerOn && opacity > 0) ensureGeometry();
  }

  /** Per frame: the shell follows the camera; the pixel ratio follows the renderer. */
  function update(camera, renderer) {
    if (!points) return;
    if (renderer && typeof renderer.getPixelRatio === 'function') uniforms.uPixelRatio.value = Math.min(2, Math.max(0.5, renderer.getPixelRatio()));
    if (mode === 'shell' && camera) group.position.copy(camera.position);
  }

  /**
   * Spec 0034 req 2: stretch every star along the screen projection of the camera's velocity `dir`
   * (scene axes), `k` of the way to STRETCH_PX. 0 is the picture as it always was. The caller -- only
   * ui/trip.js, on a rung of the ladder -- owns the envelope, the reduced-motion rule and the latch.
   */
  function setStretch(k, dir) {
    return writeStretch(uniforms, k, dir);
  }

  const _m = new THREE.Matrix4();
  const index = createPickIndex(); // scene/pickindex.js: the hover asks every 50 ms of 120 000 stars

  /**
   * Every star within the forgiveness rule, nearest first, as {record, px, score} for
   * scene/pickrank.js. An unnamed star becomes a record here and now, saying it is unnamed.
   */
  function pickAll(ndcX, ndcY, camera, viewport, limit = 6, reachPx = PICK_PX) {
    if (!points || !points.visible || !geometry || !camera || !viewport) return [];
    if (opacity < PICK_MIN_OPACITY) return []; // faded out (the climb's cross-fade): not drawn, not tapped
    camera.updateMatrixWorld();
    group.updateMatrixWorld();
    _m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(group.matrixWorld);
    const e = _m.elements;
    const attr = geometry.getAttribute('position');
    const halfW = viewport.w * 0.5, halfH = viewport.h * 0.5;
    const shell = mode === 'shell';
    const cp = camera.position, upp = uniforms.uUnitsPerPc.value, lim = uniforms.uMagLimit.value;
    const out = [];
    index.query(e, attr.array, attr.version, data.count, halfW, halfH, ndcX, ndcY, reachPx, (i, px, sx, sy, depth) => {
      if (i === hiddenIdx) return; // (the one drawn as a disc is that disc's)
      // Not drawn is not tapped: the shader's own magnitude cut, in both modes (the stellar rung's is the magnitude AT THE CAMERA).
      if (apparentMagOf(data.appMag[i], data.absMag[i], shell, attr.array[i * 3], attr.array[i * 3 + 1], attr.array[i * 3 + 2], cp, upp) > lim) return;
      out.push({ i, px, sx, sy, depth });
    });
    out.sort((p, q) => p.px - q.px);
    return out.slice(0, Math.max(1, limit)).map(({ i, px, sx, sy, depth }) => ({ record: recordAt(i), px, score: px, sx, sy, depth }));
  }

  /** The record for star `i` of the binary: the named one if it has a name, a plain one if not. */
  function recordAt(i) {
    if (byIndex.has(i)) return byIndex.get(i); // 0079 r15-a45
    const ref = data.nameRef[i];
    if (ref > 0 && namedRows && namedRows[ref - 1]) {
      const rec = records.find((r) => r.meta.starIndex === namedRows[ref - 1][0]);
      if (rec) return rec;
    }
    const x = data.posLy[i * 3], y = data.posLy[i * 3 + 1], z = data.posLy[i * 3 + 2];
    const distLy = Math.hypot(x, y, z);
    // The card's distance is the catalogue's; the mark goes where the star is drawn (scene/clusters.js).
    const at = data.moved.has(i) ? { x: data.drawLy[i * 3], y: data.drawLy[i * 3 + 1], z: data.drawLy[i * 3 + 2] } : { x, y, z };
    return starRecord(i, t(COPY.stars.unnamed, { n: String(i) }), at, {
      distLy: Math.round(distLy * 100) / 100,
      mag: Math.round(data.appMag[i] * 100) / 100,
      named: false,
    });
  }

  function dispose() {
    if (geometry) geometry.dispose();
    if (sunPoints) sunPoints.geometry.dispose();
    if (points && points.material) points.material.dispose();
    if (scene) scene.remove(group);
    data = null; geometry = null; points = null; sunPoints = null; builtFor = null;
  }

  // One star's point switched off while scene/stardisc.js draws that star as a disc: the point is a
  // float32 position a light-year scale away, and beside a disc drawn to the kilometre it sat tens
  // of degrees off its own star.
  let hiddenIdx = -1;
  let hiddenMags = null;
  function hidePoint(idx) {
    const next = Number.isInteger(idx) && data && idx >= 0 && idx < data.count ? idx : -1;
    if (next === hiddenIdx || !geometry) return;
    if (hiddenIdx >= 0 && hiddenMags) { data.absMag[hiddenIdx] = hiddenMags[0]; data.appMag[hiddenIdx] = hiddenMags[1]; }
    hiddenIdx = next;
    hiddenMags = next >= 0 ? [data.absMag[next], data.appMag[next]] : null;
    if (next >= 0) { data.absMag[next] = 99; data.appMag[next] = 99; }
    geometry.getAttribute('aAbsMag').needsUpdate = true;
    geometry.getAttribute('aAppMag').needsUpdate = true;
  }
  /** A star record's width and temperature (starPhysical), or null before the catalogue has loaded. */
  function physicalOf(record) {
    const idx = record && record.meta ? record.meta.starIndex : null;
    if (record && record.meta && record.meta.measuredRadiusSuns > 0) {
      // A paper that gives a radius and no temperature (an interferometer measures the width): the
      // colour then stays the colour-index estimate, and only the width says "measured".
      let teffK = record.meta.measuredTeffK;
      if (!(teffK > 0) && data && Number.isInteger(idx) && idx >= 0 && idx < data.count) teffK = starPhysical(data.absMag[idx], data.ci[idx]).teffK;
      return { radiusKm: SUN_RADIUS_KM * record.meta.measuredRadiusSuns, teffK, how: 'measured' };
    }
    if (!data || !Number.isInteger(idx) || idx < 0 || idx >= data.count) return null;
    const absMag = idx === hiddenIdx && hiddenMags ? hiddenMags[0] : data.absMag[idx];
    return starPhysical(absMag, data.ci[idx]);
  }

  return {
    load,
    ensureGeometry,
    hidePoint,
    physicalOf,
    records: () => records,
    count: () => (data ? data.count : namedRows ? null : null),
    unplaced: () => (data ? data.unplaced : null),
    setVisible,
    setOpacity,
    setPointScale,
    pointScale: () => uniforms.uScale.value,
    setHoles,
    holeCount: () => uniforms.uHoleCount.value,
    setMagLimit,
    setStretch,
    /** What the shader is drawing: tests and the browser check read it. */
    stretch: () => uniforms.uStretch.value,
    rebuild,
    update,
    pickAll,
    recordAt,
    dispose,
    group,
    /** For tests: which mode the buffers were last built for. */
    mode: () => mode,
  };
}

// 0079 r15-a45: begin
// scene/unplaced.js draws the stars HYG has no distance for with this very shader, so a star of one magnitude is the same light in either file.
export { VERT as STARS3D_VERT, FRAG as STARS3D_FRAG };
// 0079 r15-a45: end
